'use client';

import { AuthError, AuthErrorType } from '@/lib/services/auth-service';
import { Session } from '@supabase/supabase-js';

/**
 * Authentication event types
 */
export type AuthEventType = 
  | 'login'           // User logged in
  | 'logout'          // User logged out
  | 'refresh'         // Token was refreshed
  | 'rotation'        // Token was rotated
  | 'error'           // Authentication error occurred
  | 'warning'         // Authentication warning (non-critical issue)
  | 'recovery'        // Recovery from an authentication issue
  | 'fallback'        // Authentication fallback mechanism used
  | 'profile_fetch'   // Profile data fetched
  | 'session_expired' // Session expired
  | 'token_validation' // Token validation check
  | 'request_auth'    // Request authentication check
  | 'security_warning'; // Security-related warning

/**
 * Authentication event interface
 */
export interface AuthEvent {
  type: AuthEventType;
  userId?: string;
  timestamp: number;
  details: Record<string, any>;
  error?: Error | string;
  severity: 'info' | 'warning' | 'error';
  source: 'client' | 'server' | 'middleware';
  route?: string;
}

/**
 * Event names for auth monitoring
 */
const AUTH_ERROR_EVENT = 'auth_error';
const AUTH_FALLBACK_EVENT = 'auth_fallback';
const AUTH_RECOVERY_EVENT = 'auth_recovery';
const AUTH_EVENT = 'auth_event';

/**
 * Maximum number of events to keep in memory
 */
const MAX_EVENT_HISTORY = 100;
const MAX_ERROR_HISTORY = 50;

/**
 * Interface for error metrics
 */
interface ErrorMetrics {
  count: number;
  lastTimestamp: number;
  errors: Array<{
    type: AuthErrorType;
    message: string;
    timestamp: number;
    recovered: boolean;
    route: string;
  }>;
}

/**
 * In-memory storage for error metrics
 */
const errorMetrics: Record<AuthErrorType, ErrorMetrics> = {
  [AuthErrorType.AUTHENTICATION_ERROR]: { count: 0, lastTimestamp: 0, errors: [] },
  [AuthErrorType.PROFILE_ERROR]: { count: 0, lastTimestamp: 0, errors: [] },
  [AuthErrorType.SERVER_ERROR]: { count: 0, lastTimestamp: 0, errors: [] },
  [AuthErrorType.NETWORK_ERROR]: { count: 0, lastTimestamp: 0, errors: [] },
  [AuthErrorType.UNKNOWN_ERROR]: { count: 0, lastTimestamp: 0, errors: [] },
};

/**
 * In-memory storage for auth events
 */
const authEvents: AuthEvent[] = [];

/**
 * Fallback metrics
 */
const fallbackMetrics = {
  attempts: 0,
  successes: 0,
  failures: 0,
  specializedRecoveries: 0,
  lastTimestamp: 0,
  // Track attempts and outcomes by error type
  byErrorType: {
    [AuthErrorType.AUTHENTICATION_ERROR]: { attempts: 0, successes: 0 },
    [AuthErrorType.PROFILE_ERROR]: { attempts: 0, successes: 0 },
    [AuthErrorType.SERVER_ERROR]: { attempts: 0, successes: 0 },
    [AuthErrorType.NETWORK_ERROR]: { attempts: 0, successes: 0 },
    [AuthErrorType.UNKNOWN_ERROR]: { attempts: 0, successes: 0 }
  },
  // Track performance metrics
  performance: {
    avgRecoveryTimeMs: 0,
    totalRecoveryTimeMs: 0,
    recoveryCount: 0,
    recoveryTimestamps: [] as Array<{ start: number, end: number, duration: number, success: boolean }>
  }
};

/**
 * Safely extracts userId from session
 */
function getUserIdFromSession(session?: Session | null): string | undefined {
  return session?.user?.id;
}

/**
 * Enhanced authentication monitoring system
 */
export const authMonitoring = {
  /**
   * Log an authentication event
   * @param event The event to log
   */
  logAuthEvent: (event: AuthEvent): void => {
    // Add timestamp if not already present
    if (!event.timestamp) {
      event.timestamp = Date.now();
    }
    
    // Add route if on client and not already present
    if (!event.route && typeof window !== 'undefined') {
      event.route = window.location.pathname;
    }
    
    // Store the event in memory
    authEvents.unshift(event);
    
    // Keep only the most recent events
    if (authEvents.length > MAX_EVENT_HISTORY) {
      authEvents.pop();
    }
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development' || process.env.AUTH_DEBUG === 'true') {
      const level = event.severity === 'error' ? 'error' : 
                   event.severity === 'warning' ? 'warn' : 'log';
      
      const consoleMethod = console[level as 'log' | 'warn' | 'error'];
      
      consoleMethod(`[AuthMonitoring] ${event.type.toUpperCase()} (${event.source}) ${event.userId ? `User: ${event.userId}` : ''}`);
      
      if (Object.keys(event.details).length > 0) {
        console.group('Details');
        Object.entries(event.details).forEach(([key, value]) => {
          console.log(`${key}: `, value);
        });
        console.groupEnd();
      }
      
      if (event.error) {
        console.group('Error');
        console.error(event.error);
        console.groupEnd();
      }
    }
    
    // Dispatch event for external monitoring
    if (typeof window !== 'undefined') {
      // Dispatch a general auth event
      const customEvent = new CustomEvent(AUTH_EVENT, { detail: event });
      window.dispatchEvent(customEvent);
      
      // Also dispatch specific event types for backward compatibility
      if (event.type === 'error' && event.severity === 'error') {
        window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { detail: event }));
      } else if (event.type === 'fallback') {
        window.dispatchEvent(new CustomEvent(AUTH_FALLBACK_EVENT, { detail: event }));
      } else if (event.type === 'recovery') {
        window.dispatchEvent(new CustomEvent(AUTH_RECOVERY_EVENT, { detail: event }));
      }
      
      // Send to analytics if configured
      if (window.analytics) {
        window.analytics.track(`Auth ${event.type.charAt(0).toUpperCase() + event.type.slice(1)}`, {
          userId: event.userId,
          severity: event.severity,
          source: event.source,
          route: event.route,
          ...event.details,
          errorMessage: event.error instanceof Error ? event.error.message : event.error
        });
      }
    }
  },
  
  /**
   * Log a login event
   * @param session The user session
   * @param details Additional details about the login
   * @param source Where the login was performed (client/server/middleware)
   */
  logLogin: (session: Session | null, details: Record<string, any> = {}, source: 'client' | 'server' | 'middleware' = 'client'): void => {
    authMonitoring.logAuthEvent({
      type: 'login',
      userId: getUserIdFromSession(session),
      timestamp: Date.now(),
      details: {
        method: details.method || 'password',
        provider: details.provider,
        isNewUser: details.isNewUser,
        hasProfile: details.hasProfile,
        ...details
      },
      severity: 'info',
      source,
      route: details.route
    });
  },
  
  /**
   * Log a logout event
   * @param userId The user ID
   * @param details Additional details about the logout
   * @param source Where the logout was performed
   */
  logLogout: (userId?: string, details: Record<string, any> = {}, source: 'client' | 'server' | 'middleware' = 'client'): void => {
    authMonitoring.logAuthEvent({
      type: 'logout',
      userId,
      timestamp: Date.now(),
      details: {
        reason: details.reason || 'user_initiated',
        sessionDuration: details.sessionDuration,
        ...details
      },
      severity: 'info',
      source,
      route: details.route
    });
  },
  
  /**
   * Log a token refresh event
   * @param session The new session
   * @param details Additional details about the refresh
   * @param source Where the refresh was performed
   */
  logRefresh: (session: Session | null, details: Record<string, any> = {}, source: 'client' | 'server' | 'middleware' = 'client'): void => {
    authMonitoring.logAuthEvent({
      type: 'refresh',
      userId: getUserIdFromSession(session),
      timestamp: Date.now(),
      details: {
        success: details.success !== false,
        reason: details.reason || 'expiring',
        expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : undefined,
        ...details
      },
      severity: details.success === false ? 'warning' : 'info',
      source,
      route: details.route
    });
  },
  
  /**
   * Log a token rotation event
   * @param session The new session
   * @param details Additional details about the rotation
   * @param source Where the rotation was performed
   */
  logRotation: (session: Session | null, details: Record<string, any> = {}, source: 'client' | 'server' | 'middleware' = 'client'): void => {
    authMonitoring.logAuthEvent({
      type: 'rotation',
      userId: getUserIdFromSession(session),
      timestamp: Date.now(),
      details: {
        success: details.success !== false,
        forced: details.forced || false,
        reason: details.reason || 'expiring',
        expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : undefined,
        ...details
      },
      severity: details.success === false ? 'warning' : 'info',
      source,
      route: details.route
    });
  },
  
  /**
   * Log a token validation event
   * @param valid Whether the token is valid
   * @param details Additional details about the validation
   * @param source Where the validation was performed
   */
  logTokenValidation: (valid: boolean, details: Record<string, any> = {}, source: 'client' | 'server' | 'middleware' = 'server'): void => {
    authMonitoring.logAuthEvent({
      type: 'token_validation',
      userId: details.userId,
      timestamp: Date.now(),
      details: {
        valid,
        reason: details.reason,
        tokenType: details.tokenType || 'access',
        ...details
      },
      severity: valid ? 'info' : 'warning',
      source,
      route: details.route
    });
  },
  
  /**
   * Log an authentication error
   * @param error The error that occurred
   * @param details Additional details about the error
   * @param source Where the error occurred
   */
  logError: (error: Error | string, details: Record<string, any> = {}, source: 'client' | 'server' | 'middleware' = 'client'): void => {
    authMonitoring.logAuthEvent({
      type: 'error',
      userId: details.userId,
      timestamp: Date.now(),
      details: {
        errorType: details.errorType || 'auth_error',
        recoverable: details.recoverable !== false,
        operation: details.operation,
        ...details
      },
      error,
      severity: 'error',
      source,
      route: details.route
    });
    
    // For backward compatibility with the original AuthMonitoring
    // Removed legacy AuthError handling due to dependency changes
  },
  
  /**
   * Log a security warning (potential issues)
   * @param warning The warning message
   * @param details Additional details about the warning
   * @param source Where the warning was detected
   */
  logSecurityWarning: (warning: string, details: Record<string, any> = {}, source: 'client' | 'server' | 'middleware' = 'server'): void => {
    authMonitoring.logAuthEvent({
      type: 'security_warning',
      userId: details.userId,
      timestamp: Date.now(),
      details: {
        warning,
        severity: details.severity || 'medium',
        mitigated: details.mitigated || false,
        ...details
      },
      severity: 'warning',
      source,
      route: details.route
    });
  },
  
  /**
   * Get recent authentication events
   * @param count Number of events to retrieve
   * @param filter Optional filter for event types
   * @returns Recent auth events
   */
  getRecentEvents: (count: number = 20, filter?: AuthEventType | AuthEventType[]): AuthEvent[] => {
    // If no filter, return most recent events
    if (!filter) {
      return authEvents.slice(0, count);
    }
    
    // Convert single type to array
    const filterTypes = Array.isArray(filter) ? filter : [filter];
    
    // Filter events by type and take requested count
    return authEvents
      .filter(event => filterTypes.includes(event.type))
      .slice(0, count);
  },
  
  /**
   * Get events for a specific user
   * @param userId User ID to filter by
   * @param count Maximum number of events to return
   * @returns User's auth events
   */
  getUserEvents: (userId: string, count: number = 20): AuthEvent[] => {
    return authEvents
      .filter(event => event.userId === userId)
      .slice(0, count);
  },
  
  /**
   * Detect anomalies in authentication patterns
   * @returns Events that represent anomalies
   */
  detectAnomalies: (): AuthEvent[] => {
    const anomalies: AuthEvent[] = [];
    const now = Date.now();
    
    // Look for unusual patterns in the event history
    
    // 1. Multiple failed login attempts
    const recentFailedLogins = authEvents.filter(
      e => e.type === 'error' && 
           e.details.operation === 'login' && 
           e.timestamp > now - 15 * 60 * 1000 // Last 15 minutes
    );
    
    // Group by userId or IP
    const failedLoginsByUser: Record<string, AuthEvent[]> = {};
    recentFailedLogins.forEach(event => {
      const key = event.userId || event.details.ip || 'unknown';
      if (!failedLoginsByUser[key]) {
        failedLoginsByUser[key] = [];
      }
      failedLoginsByUser[key].push(event);
    });
    
    // Check for users with multiple failed attempts
    Object.entries(failedLoginsByUser).forEach(([userId, events]) => {
      if (events.length >= 5) {
        anomalies.push({
          type: 'security_warning',
          userId: userId !== 'unknown' ? userId : undefined,
          timestamp: now,
          details: {
            anomalyType: 'multiple_failed_logins',
            count: events.length,
            timeWindow: '15 minutes',
            firstAttempt: new Date(events[events.length - 1].timestamp).toISOString(),
            lastAttempt: new Date(events[0].timestamp).toISOString(),
          },
          severity: 'warning',
          source: 'server',
        });
      }
    });
    
    // 2. Frequent token refreshes (potential token theft)
    const recentRefreshes = authEvents.filter(
      e => (e.type === 'refresh' || e.type === 'rotation') && 
           e.timestamp > now - 60 * 60 * 1000 // Last hour
    );
    
    // Group by userId
    const refreshesByUser: Record<string, AuthEvent[]> = {};
    recentRefreshes.forEach(event => {
      if (!event.userId) return;
      
      if (!refreshesByUser[event.userId]) {
        refreshesByUser[event.userId] = [];
      }
      refreshesByUser[event.userId].push(event);
    });
    
    // Check for users with excessive refreshes
    Object.entries(refreshesByUser).forEach(([userId, events]) => {
      if (events.length >= 10) { // Threshold for suspicious activity
        anomalies.push({
          type: 'security_warning',
          userId,
          timestamp: now,
          details: {
            anomalyType: 'excessive_token_refreshes',
            count: events.length,
            timeWindow: '1 hour',
            refreshInterval: events.length > 1 ? 
              (events[0].timestamp - events[events.length - 1].timestamp) / events.length : 
              0,
          },
          severity: 'warning',
          source: 'server',
        });
      }
    });
    
    // 3. Geographic anomalies (requires IP data)
    // This is a placeholder for future implementation
    
    return anomalies;
  },
  
  /**
   * Clear authentication events (for privacy or testing)
   * @param olderThan Only clear events older than this timestamp
   */
  clearEvents: (olderThan?: number): void => {
    if (olderThan) {
      const index = authEvents.findIndex(event => event.timestamp < olderThan);
      if (index !== -1) {
        authEvents.splice(index);
      }
    } else {
      authEvents.length = 0;
    }
  },
  
  /**
   * Track authentication metrics over time
   * @returns Authentication metrics
   */
  getAuthMetrics: () => {
    const now = Date.now();
    const oneDayAgo = now - 24 * 60 * 60 * 1000;
    const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000;
    
    // Events in the last day
    const dayEvents = authEvents.filter(e => e.timestamp >= oneDayAgo);
    
    // Events in the last week
    const weekEvents = authEvents.filter(e => e.timestamp >= oneWeekAgo);
    
    // Count by type for day
    const dayCountByType: Record<AuthEventType, number> = {} as Record<AuthEventType, number>;
    dayEvents.forEach(event => {
      dayCountByType[event.type] = (dayCountByType[event.type] || 0) + 1;
    });
    
    // Count by type for week
    const weekCountByType: Record<AuthEventType, number> = {} as Record<AuthEventType, number>;
    weekEvents.forEach(event => {
      weekCountByType[event.type] = (weekCountByType[event.type] || 0) + 1;
    });
    
    // Calculate login success rate
    const dayLogins = dayEvents.filter(e => e.type === 'login' || 
                                      (e.type === 'error' && e.details.operation === 'login'));
    const dayLoginSuccesses = dayEvents.filter(e => e.type === 'login');
    const dayLoginSuccessRate = dayLogins.length > 0 ? 
      dayLoginSuccesses.length / dayLogins.length * 100 : 100;
    
    const weekLogins = weekEvents.filter(e => e.type === 'login' || 
                                       (e.type === 'error' && e.details.operation === 'login'));
    const weekLoginSuccesses = weekEvents.filter(e => e.type === 'login');
    const weekLoginSuccessRate = weekLogins.length > 0 ? 
      weekLoginSuccesses.length / weekLogins.length * 100 : 100;
    
    return {
      dailyMetrics: {
        totalEvents: dayEvents.length,
        countByType: dayCountByType,
        loginSuccessRate: dayLoginSuccessRate,
        errorCount: dayEvents.filter(e => e.severity === 'error').length,
        warningCount: dayEvents.filter(e => e.severity === 'warning').length,
      },
      weeklyMetrics: {
        totalEvents: weekEvents.length,
        countByType: weekCountByType,
        loginSuccessRate: weekLoginSuccessRate,
        errorCount: weekEvents.filter(e => e.severity === 'error').length,
        warningCount: weekEvents.filter(e => e.severity === 'warning').length,
      },
      userActivityRate: weekEvents.reduce((userSet, event) => {
        if (event.userId) userSet.add(event.userId);
        return userSet;
      }, new Set<string>()).size,
    };
  }
};

/**
 * Legacy authentication monitoring system
 * Maintained for backward compatibility
 */
export const AuthMonitoring = {
  /**
   * Track an authentication error
   * @param error The error to track
   * @param recovered Whether the error was recovered from
   */
  trackError: (error: AuthError, recovered: boolean = false) => {
    const now = Date.now();
    const route = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
    
    // Update error metrics
    const metrics = errorMetrics[error.type] || errorMetrics[AuthErrorType.UNKNOWN_ERROR];
    metrics.count++;
    metrics.lastTimestamp = now;
    
    // Add to error history, keeping only the most recent errors
    metrics.errors.unshift({
      type: error.type,
      message: error.message,
      timestamp: now,
      recovered,
      route
    });
    
    if (metrics.errors.length > MAX_ERROR_HISTORY) {
      metrics.errors = metrics.errors.slice(0, MAX_ERROR_HISTORY);
    }
    
    // Dispatch event for monitoring
    if (typeof window !== 'undefined') {
      const event = new CustomEvent(AUTH_ERROR_EVENT, { 
        detail: { 
          error, 
          recovered,
          timestamp: now,
          route
        } 
      });
      window.dispatchEvent(event);
      
      // Send to analytics or monitoring service if configured
      if (window.analytics) {
        window.analytics.track('Auth Error', {
          errorType: error.type,
          message: error.message,
          recovered,
          route
        });
      }
    }
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group('[AuthMonitoring] Auth Error Tracked');
      console.log('Type:', error.type);
      console.log('Message:', error.message);
      console.log('Recovered:', recovered);
      console.log('Route:', route);
      console.groupEnd();
    }
  },
  
  /**
   * Track a fallback attempt
   * @param success Whether the fallback was successful
   * @param errorType The type of error being recovered from
   * @param startTimestamp Optional timestamp when the recovery attempt started
   */
  trackFallback: (
    success: boolean, 
    errorType: AuthErrorType = AuthErrorType.UNKNOWN_ERROR, 
    startTimestamp?: number
  ) => {
    const now = Date.now();
    const recoveryStartTime = startTimestamp || now;
    
    // Update fallback metrics
    fallbackMetrics.attempts++;
    fallbackMetrics.lastTimestamp = now;
    
    // Update metrics by error type
    if (fallbackMetrics.byErrorType[errorType]) {
      fallbackMetrics.byErrorType[errorType].attempts++;
      if (success) {
        fallbackMetrics.byErrorType[errorType].successes++;
      }
    }
    
    if (success) {
      fallbackMetrics.successes++;
      
      // Update performance metrics
      if (startTimestamp) {
        const duration = now - startTimestamp;
        fallbackMetrics.performance.recoveryCount++;
        fallbackMetrics.performance.totalRecoveryTimeMs += duration;
        fallbackMetrics.performance.avgRecoveryTimeMs = 
          fallbackMetrics.performance.totalRecoveryTimeMs / fallbackMetrics.performance.recoveryCount;
        
        // Add to timestamp history, keeping last 20 only
        fallbackMetrics.performance.recoveryTimestamps.unshift({
          start: startTimestamp,
          end: now,
          duration,
          success: true
        });
        
        if (fallbackMetrics.performance.recoveryTimestamps.length > 20) {
          fallbackMetrics.performance.recoveryTimestamps.pop();
        }
      }
    } else {
      fallbackMetrics.failures++;
      
      // Update performance metrics for failed attempts too
      if (startTimestamp) {
        const duration = now - startTimestamp;
        
        // Add to timestamp history, keeping last 20 only
        fallbackMetrics.performance.recoveryTimestamps.unshift({
          start: startTimestamp,
          end: now,
          duration,
          success: false
        });
        
        if (fallbackMetrics.performance.recoveryTimestamps.length > 20) {
          fallbackMetrics.performance.recoveryTimestamps.pop();
        }
      }
    }
    
    // Dispatch event for monitoring
    if (typeof window !== 'undefined') {
      const event = new CustomEvent(AUTH_FALLBACK_EVENT, { 
        detail: { 
          success,
          errorType,
          timestamp: now,
          route: window.location.pathname,
          duration: startTimestamp ? now - startTimestamp : undefined
        } 
      });
      window.dispatchEvent(event);
      
      // Send to analytics or monitoring service if configured
      if (window.analytics) {
        window.analytics.track('Auth Fallback', {
          success,
          errorType,
          route: window.location.pathname,
          duration: startTimestamp ? now - startTimestamp : undefined
        });
      }
    }
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      const durationStr = startTimestamp ? ` (took ${now - startTimestamp}ms)` : '';
      console.log(
        `[AuthMonitoring] Auth Fallback for ${errorType} ${success ? 'Succeeded' : 'Failed'}${durationStr}`
      );
    }
  },
  
  /**
   * Track a successful recovery from an error
   * @param method The recovery method used
   * @param errorType The type of error that was recovered from
   * @param details Additional details about the recovery
   */
  trackRecovery: (
    method: 'fallback' | 'retry' | 'refresh' | 'specialized', 
    errorType: AuthErrorType = AuthErrorType.UNKNOWN_ERROR,
    details?: { durationMs?: number; route?: string; specializedMethod?: string }
  ) => {
    if (method === 'specialized') {
      fallbackMetrics.specializedRecoveries++;
    }
    
    const now = Date.now();
    const route = details?.route || (typeof window !== 'undefined' ? window.location.pathname : 'unknown');
    
    if (typeof window !== 'undefined') {
      const event = new CustomEvent(AUTH_RECOVERY_EVENT, { 
        detail: { 
          method,
          errorType,
          timestamp: now,
          route,
          durationMs: details?.durationMs,
          specializedMethod: details?.specializedMethod
        } 
      });
      window.dispatchEvent(event);
      
      // Send to analytics or monitoring service if configured
      if (window.analytics) {
        window.analytics.track('Auth Recovery', {
          method,
          errorType,
          route,
          durationMs: details?.durationMs,
          specializedMethod: details?.specializedMethod
        });
        
        // Also track reliability metrics in analytics
        const successRate = fallbackMetrics.attempts > 0 
          ? (fallbackMetrics.successes / fallbackMetrics.attempts) * 100 
          : 0;
            
        window.analytics.track('Auth Reliability', {
          authFallbackSuccessRate: successRate.toFixed(2),
          authFallbackCount: fallbackMetrics.attempts,
          authFallbackSuccesses: fallbackMetrics.successes,
          specializedRecoveries: fallbackMetrics.specializedRecoveries,
          mostCommonErrorType: getMostCommonErrorType(),
          avgRecoveryTimeMs: fallbackMetrics.performance.avgRecoveryTimeMs
        });
      }
    }
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      const durationStr = details?.durationMs ? ` (took ${details.durationMs}ms)` : '';
      const specializedStr = details?.specializedMethod ? ` [${details.specializedMethod}]` : '';
      console.log(
        `[AuthMonitoring] Auth Recovery for ${errorType} via ${method}${specializedStr}${durationStr}`
      );
    }
  },
  
  /**
   * Get error metrics for a specific error type
   * @param errorType The error type to get metrics for
   */
  getErrorMetrics: (errorType?: AuthErrorType) => {
    if (errorType) {
      return { ...errorMetrics[errorType] };
    }
    
    // Aggregate metrics across all error types
    return {
      totalCount: Object.values(errorMetrics).reduce((sum, m) => sum + m.count, 0),
      byType: { ...errorMetrics },
      recentErrors: Object.values(errorMetrics)
        .flatMap(m => m.errors)
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, MAX_ERROR_HISTORY)
    };
  },
  
  /**
   * Get fallback metrics
   * @param detailed Whether to include detailed metrics (performance history, etc.)
   */
  getFallbackMetrics: (detailed = false) => {
    const metrics = { ...fallbackMetrics };
    
    // Calculate success rate
    const successRate = metrics.attempts > 0 
      ? (metrics.successes / metrics.attempts) * 100 
      : 0;
    
    // Add calculated metrics
    const calculatedMetrics = {
      ...metrics,
      successRate: successRate.toFixed(2) + '%',
      mostCommonErrorType: getMostCommonErrorType(),
      recentActivity: metrics.lastTimestamp > 0 
        ? `${Math.round((Date.now() - metrics.lastTimestamp) / 1000)}s ago` 
        : 'none'
    };
    
    // For non-detailed requests, remove the detailed performance history
    if (!detailed && calculatedMetrics.performance) {
      calculatedMetrics.performance = {
        ...calculatedMetrics.performance,
        recoveryTimestamps: calculatedMetrics.performance.recoveryTimestamps.slice(0, 3) // Only keep most recent 3
      };
    }
    
    return calculatedMetrics;
  },
  
  /**
   * Reset error metrics (for testing)
   */
  resetMetrics: () => {
    Object.keys(errorMetrics).forEach(key => {
      const errorType = key as AuthErrorType;
      errorMetrics[errorType] = { count: 0, lastTimestamp: 0, errors: [] };
    });
    
    fallbackMetrics.attempts = 0;
    fallbackMetrics.successes = 0;
    fallbackMetrics.failures = 0;
    fallbackMetrics.lastTimestamp = 0;
  }
};

/**
 * Determine the most common error type based on error metrics
 */
const getMostCommonErrorType = (): AuthErrorType => {
  let maxCount = 0;
  let mostCommonType = AuthErrorType.UNKNOWN_ERROR;
  
  Object.entries(errorMetrics).forEach(([type, metrics]) => {
    if (metrics.count > maxCount) {
      maxCount = metrics.count;
      mostCommonType = type as AuthErrorType;
    }
  });
  
  return mostCommonType;
};

// Add type definition for analytics
declare global {
  interface Window {
    analytics?: {
      track: (event: string, properties?: any) => void;
    };
  }
}